import { useState, useEffect } from "react";
import { docsClient } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Loader2,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Play,
  History,
  Clock,
  Info,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";

interface QualityIssue {
  document_id: string;
  url: string;
  title: string | null;
  issues: string[];
  severity: "high" | "medium" | "low";
}

interface QualityReport {
  total_documents: number;
  documents_with_issues: number;
  issues_by_severity: {
    high: number;
    medium: number;
    low: number;
  };
  issues: QualityIssue[];
  stats: {
    missing_title: number;
    missing_description: number;
    insufficient_tags: number;
    short_description: number;
    no_content: number;
    failed_processing: number;
  };
}

interface MaintenanceLog {
  id: string;
  task_type: string;
  status: string;
  started_at: string;
  completed_at: string | null;
  report: QualityReport | null;
  error_message: string | null;
  created_at: string;
}

interface MaintenanceTask {
  id: string;
  task_name: string;
  task_type: string;
  status: "queued" | "running" | "completed" | "failed";
  progress: number;
  document_url: string | null;
  started_at: string | null;
  completed_at: string | null;
  result: unknown | null;
  error_message: string | null;
  created_at: string;
}

export default function MaintenancePage() {
  const [loading, setLoading] = useState(false);
  const [fixingIssues, setFixingIssues] = useState(false);
  const [report, setReport] = useState<QualityReport | null>(null);
  const [logs, setLogs] = useState<MaintenanceLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [selectedLog, setSelectedLog] = useState<MaintenanceLog | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      setLoadingLogs(true);
      const { data, error } = await docsClient
        .from("maintenance_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) throw error;
      setLogs(data || []);
    } catch (error) {
      console.error("Error loading logs:", error);
      toast({
        title: "Error",
        description: "Failed to load maintenance history",
        variant: "destructive",
      });
    } finally {
      setLoadingLogs(false);
    }
  };

  const runAutoFix = async () => {
    setFixingIssues(true);
    try {
      const {
        data: { user },
      } = await docsClient.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const {
        data: { session },
      } = await docsClient.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_DOCS_SUPABASE_URL}/functions/v1/auto-fix-quality-issues`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            user_id: user.id,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to run auto-fix");
      }

      const data = await response.json();

      toast({
        title: "Auto-Fix Complete",
        description: `Fixed ${data.total_fixed} documents`,
      });

      // Run quality check again to see updated results
      setTimeout(() => runQualityCheck(), 1000);
    } catch (error) {
      console.error("Error running auto-fix:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to run auto-fix",
        variant: "destructive",
      });
    } finally {
      setFixingIssues(false);
    }
  };

  const runQualityCheck = async () => {
    setLoading(true);
    try {
      const {
        data: { user },
      } = await docsClient.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const {
        data: { session },
      } = await docsClient.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_DOCS_SUPABASE_URL}/functions/v1/check-metadata-quality`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            user_id: user.id,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to run quality check");
      }

      const data = await response.json();
      setReport(data.report);

      toast({
        title: "Quality Check Complete",
        description: `Found ${data.report.documents_with_issues} documents with issues out of ${data.report.total_documents} total`,
      });

      // Reload logs to show new entry
      loadLogs();
    } catch (error) {
      console.error("Error running quality check:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to run quality check",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getSeverityBadge = (severity: "high" | "medium" | "low") => {
    switch (severity) {
      case "high":
        return (
          <Badge variant="outline" className="border-red-500 text-red-700 dark:text-red-400">
            High
          </Badge>
        );
      case "medium":
        return (
          <Badge variant="outline" className="border-amber-500 text-amber-700 dark:text-amber-400">
            Medium
          </Badge>
        );
      case "low":
        return (
          <Badge variant="outline" className="border-blue-500 text-blue-700 dark:text-blue-400">
            Low
          </Badge>
        );
    }
  };

  const getTaskTypeName = (taskType: string) => {
    const names: Record<string, string> = {
      quality_check: "Quality Check",
      content_update: "Content Update",
      html_cleaning: "HTML Cleaning",
      tag_organization: "Tag Organization",
      gap_analysis: "Gap Analysis",
    };
    return names[taskType] || taskType;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "running":
        return (
          <Badge variant="outline" className="border-blue-500 text-blue-700 dark:text-blue-400">
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            Running
          </Badge>
        );
      case "completed":
        return (
          <Badge variant="outline" className="border-green-500 text-green-700 dark:text-green-400">
            <CheckCircle className="mr-1 h-3 w-3" />
            Completed
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="outline" className="border-red-500 text-red-700 dark:text-red-400">
            <XCircle className="mr-1 h-3 w-3" />
            Failed
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Maintenance</h1>
        <p className="text-muted-foreground mt-2">
          Automated maintenance tasks for your documentation library
        </p>
      </div>

      <Tabs defaultValue="tasks" className="space-y-6">
        <TabsList>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
          <TabsTrigger value="history">
            <History className="mr-2 h-4 w-4" />
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="space-y-6">
          {/* Quality Check Rules */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Quality Check Rules</AlertTitle>
            <AlertDescription>
              <div className="mt-2 space-y-1 text-sm">
                <p>
                  <strong className="text-red-600 dark:text-red-400">High Priority:</strong> Missing
                  content, processing failed/pending, document too large
                </p>
                <p>
                  <strong className="text-amber-600 dark:text-amber-400">Medium Priority:</strong>{" "}
                  Missing title/description, insufficient tags (&lt;3 tags)
                </p>
                <p>
                  <strong className="text-blue-600 dark:text-blue-400">Low Priority:</strong> Short
                  description (&lt;20 characters)
                </p>
              </div>
            </AlertDescription>
          </Alert>

          {/* Quality Check Card */}
          <Card>
            <CardHeader>
              <CardTitle>Metadata Quality Check</CardTitle>
              <CardDescription>
                Scan your documentation for missing metadata, insufficient tags, and quality issues
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Button onClick={runQualityCheck} disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Running Check...
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" />
                      Run Quality Check
                    </>
                  )}
                </Button>
                {report && report.documents_with_issues > 0 && (
                  <Button onClick={runAutoFix} disabled={fixingIssues} variant="secondary">
                    {fixingIssues ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Fixing Issues...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Auto-Fix Issues
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Report Summary */}
          {report && (
            <>
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Total Documents
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{report.total_documents}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      With Issues
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{report.documents_with_issues}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      High Priority
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                      {report.issues_by_severity.high}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Medium Priority
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                      {report.issues_by_severity.medium}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Issue Statistics */}
              <Card>
                <CardHeader>
                  <CardTitle>Issue Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Missing Title</span>
                      <span className="font-semibold">{report.stats.missing_title}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Missing Description</span>
                      <span className="font-semibold">{report.stats.missing_description}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Insufficient Tags</span>
                      <span className="font-semibold">{report.stats.insufficient_tags}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Short Description</span>
                      <span className="font-semibold">{report.stats.short_description}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">No Content</span>
                      <span className="font-semibold">{report.stats.no_content}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Failed Processing</span>
                      <span className="font-semibold">{report.stats.failed_processing}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Issues Table */}
              {report.issues.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Documents with Issues</CardTitle>
                    <CardDescription>Review and fix quality issues</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Severity</TableHead>
                          <TableHead>Document</TableHead>
                          <TableHead>Issues</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.issues.map((issue) => (
                          <TableRow key={issue.document_id}>
                            <TableCell>{getSeverityBadge(issue.severity)}</TableCell>
                            <TableCell>
                              <div className="max-w-md">
                                <p className="font-medium truncate">
                                  {issue.title || (
                                    <span className="text-muted-foreground">Untitled</span>
                                  )}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {issue.url}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {issue.issues.map((issueText, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">
                                    {issueText}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Success Message */}
              {report.documents_with_issues === 0 && (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertTitle>All Clear!</AlertTitle>
                  <AlertDescription>
                    All {report.total_documents} documents have complete metadata and are properly
                    organized.
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Maintenance History</CardTitle>
              <CardDescription>
                View past maintenance task executions and their results
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingLogs ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : logs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No maintenance tasks have been run yet
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Task</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Summary</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => {
                      const duration = log.completed_at
                        ? Math.round(
                            (new Date(log.completed_at).getTime() -
                              new Date(log.started_at).getTime()) /
                              1000
                          )
                        : null;

                      return (
                        <TableRow
                          key={log.id}
                          className={
                            log.status === "completed" && log.report
                              ? "cursor-pointer hover:bg-muted/50"
                              : ""
                          }
                          onClick={() => {
                            if (log.status === "completed" && log.report) {
                              setSelectedLog(log);
                              setReport(log.report as QualityReport);
                            }
                          }}
                        >
                          <TableCell className="font-medium">
                            {getTaskTypeName(log.task_type)}
                          </TableCell>
                          <TableCell>{getStatusBadge(log.status)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDistanceToNow(new Date(log.started_at), { addSuffix: true })}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {duration ? `${duration}s` : "-"}
                          </TableCell>
                          <TableCell>
                            {log.status === "completed" && log.report && (
                              <div className="text-sm">
                                {log.task_type === "quality_check" && (
                                  <span>
                                    {log.report.documents_with_issues} issues found in{" "}
                                    {log.report.total_documents} documents
                                  </span>
                                )}
                              </div>
                            )}
                            {log.status === "failed" && (
                              <span className="text-sm text-destructive">
                                {log.error_message || "Task failed"}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Selected Log Report Details */}
          {selectedLog && selectedLog.report && (
            <>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  Report from{" "}
                  {formatDistanceToNow(new Date(selectedLog.started_at), { addSuffix: true })}
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setSelectedLog(null)}>
                  Clear
                </Button>
              </div>

              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Total Documents
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{report?.total_documents}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      With Issues
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{report?.documents_with_issues}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      High Priority
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                      {report?.issues_by_severity.high}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Medium Priority
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                      {report?.issues_by_severity.medium}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Issue Statistics */}
              <Card>
                <CardHeader>
                  <CardTitle>Issue Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Missing Title</span>
                      <span className="font-semibold">{report?.stats.missing_title}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Missing Description</span>
                      <span className="font-semibold">{report?.stats.missing_description}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Insufficient Tags</span>
                      <span className="font-semibold">{report?.stats.insufficient_tags}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Short Description</span>
                      <span className="font-semibold">{report?.stats.short_description}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">No Content</span>
                      <span className="font-semibold">{report?.stats.no_content}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Failed Processing</span>
                      <span className="font-semibold">{report?.stats.failed_processing}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Issues Table */}
              {report && report.issues.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Documents with Issues</CardTitle>
                    <CardDescription>Review and fix quality issues</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Severity</TableHead>
                          <TableHead>Document</TableHead>
                          <TableHead>Issues</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.issues.map((issue) => (
                          <TableRow key={issue.document_id}>
                            <TableCell>{getSeverityBadge(issue.severity)}</TableCell>
                            <TableCell>
                              <div className="max-w-md">
                                <p className="font-medium truncate">
                                  {issue.title || (
                                    <span className="text-muted-foreground">Untitled</span>
                                  )}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {issue.url}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {issue.issues.map((issueText, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">
                                    {issueText}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
